'use strict'

const BasUtil = require('@basalte/bas-util')

angular
  .module('basalteApp')
  .service('BasSecurity', [
    '$rootScope',
    'BAS_CURRENT_CORE',
    'BAS_INPUT_MODAL',
    'BAS_API',
    'SECURITY',
    'BasInputModal',
    'CurrentBasCore',
    'BasUtilities',
    'BasAppDevice',
    BasSecurity
  ])

/**
 * @typedef {Object} TSecurityAlarmState
 * @property {TBasAlarmSystem | null} alarmSystem
 * @property {string} currentSystemUuid
 * @property {TBasAlarm | null} selectedAlarm
 * @property {boolean} isArmed
 * @property {boolean} isTriggered
 * @property {boolean} canArm
 * @property {boolean} canDisarm
 * @property {int} countdown
 */

/**
 * @param $rootScope
 * @param {BAS_CURRENT_CORE} BAS_CURRENT_CORE
 * @param {BAS_INPUT_MODAL} BAS_INPUT_MODAL
 * @param {BAS_API} BAS_API
 * @param {SECURITY} SECURITY
 * @param {BasInputModal} BasInputModal
 * @param {CurrentBasCore} CurrentBasCore
 * @param {BasUtilities} BasUtilities
 */

function BasSecurity (
  $rootScope,
  BAS_CURRENT_CORE,
  BAS_INPUT_MODAL,
  BAS_API,
  SECURITY,
  BasInputModal,
  CurrentBasCore,
  BasUtilities,
  BasAppDevice
) {

  let _securityTopicListeners = []

  /**
   * @type {TCurrentBasCoreState}
   */
  const currentBasCoreState = CurrentBasCore.get()

  /**
   * @type {TSecurityAlarmState}
   */
  const state = {
    alarmSystem: null,
    selectedAlarm: null,
    canArm: false,
    canDisarm: false,
    countdown: -1
  }

  const lockout = {
    isActive: false,
    unlockAt: 0,
    timer: null,
    durationMs: 60000
  }

  this.get = get
  this.getAlarmSystems = getAlarmSystems
  this.showInputModal = showInputModal
  this.arm = arm
  this.disarm = disarm
  this.clearState = clearState
  this.cancelCountdown = cancelCountdown
  this.isLockedOut = isLockedOut
  this.getLockoutRemaining = getLockoutRemaining
  this.startLockout = startLockout
  this.getLockoutDuration = getLockoutDuration
  this.decrementCounterDemo = decrementCounterDemo

  init()

  function init () {

    $rootScope.$on(
      SECURITY.EVT_SENSOR_STATE_CHANGED,
      () => $rootScope.$applyAsync()
    )

    if (BasAppDevice.isCoreClient()) {

      $rootScope.$on(
        BAS_CURRENT_CORE.EVT_CORE_CORE_V2_CONNECTED,
        (_event, _core, isConnected) => {

          if (isConnected) {

            _getSecurityTopic()?.subscribe().then(_onSecurityTopicAvailable)
          } else {
            BasUtil.executeArray(_securityTopicListeners)
            _securityTopicListeners = []
          }
        }
      )
    }
  }

  function _onSecurityTopicAvailable () {
    const securityTopic = _getSecurityTopic()

    getAlarmSystems()

    _securityTopicListeners.push(BasUtil.setEventListener(
      securityTopic,
      BAS_API.SecurityTopic.EVT_SYSTEM_STATE_UPDATED,
      (evt) => {
        if (evt.state === SECURITY.BAS_SYSTEM_STATE_ARMING) {
          if (typeof evt.pendingTime === 'number') {
            state.countdown = evt.pendingTime
            $rootScope.$emit(SECURITY.EVT_COUNTDOWN_UPDATED, state.countdown)
          } else {
            // Fallback: if pendingTime is not provided
            decrementCounter()

          }
        } else if (
          evt.state === SECURITY.BAS_SYSTEM_STATE_ARMED ||
          state.countdown >= 0
        ) {
          // Prevent the countdown modal from getting stuck.
          // Cancel the countdown if the server reports 'armed',
          // even when pendingTime shows 1 second remaining. (server issue)
          $rootScope.$emit(SECURITY.EVT_FORCE_CLOSE_COUNTDOWN_MODAL)
          cancelCountdown()
        }
        _onSystemStateChanged(evt)
      }
    ))

    _securityTopicListeners.push(BasUtil.setEventListener(
      securityTopic,
      BAS_API.SecurityTopic.EVT_SENSOR_STATE_CHANGED,
      (data) => _onSensorStateChanged(data)
    ))
  }

  function getAlarmSystems () {

    const securityTopic = _getSecurityTopic()

    return securityTopic.getAlarmSystems()
      .then((alarmSystems) => {
        if (alarmSystems.length) {

          SECURITY.SYSTEM_STATE.hasSystem = true

          state.alarmSystem = alarmSystems[0]
          _syncAlarmSystemstate()
          _syncAlarmSystemCapabilities()

          if (alarmSystems[0].timeoutDurationSeconds) {
            lockout.durationMs = alarmSystems[0].timeoutDurationSeconds * 1000
          }
          $rootScope.$emit(SECURITY.EVT_SYSTEM_STATE_CHANGED)
          $rootScope.$applyAsync()
        }
      })
  }

  function _onSystemStateChanged (data) {
    if (state.alarmSystem.uuid !== data.systemUuid) return state.alarmSystem

    if (data.state !== SECURITY.BAS_SYSTEM_STATE_ARMING) {
      state.alarmSystem.state = data.state
      _syncAlarmSystemstate()
      _syncAlarmSystemCapabilities()
    }

    $rootScope.$emit(SECURITY.EVT_SYSTEM_STATE_CHANGED)
    $rootScope.$applyAsync()
  }

  /**
   * @param {Object} data
   */
  function _onSensorStateChanged (data) {
    const area =
      state.alarmSystem?.alarmAreas.find(a => a.uuid === data.alarmAreaUuid)

    const sensor = data.groupUuid === undefined
      ? area?.sensors.find(s => s.uuid === data.sensorUuid)
      : area?.sensorGroups.find(g => g.uuid === data.groupUuid)?.sensors.find(
        s => s.uuid === data.sensorUuid
      )

    if (!sensor) return state.alarmSystem

    sensor.state = data.state

    $rootScope.$emit(SECURITY.EVT_SENSOR_STATE_CHANGED)
    $rootScope.$applyAsync()
  }

  function _syncAlarmSystemstate () {
    state.isArmed =
      state.alarmSystem.state === SECURITY.BAS_SYSTEM_STATE_ARMED
    state.isTriggered =
      state.alarmSystem.state === SECURITY.BAS_SYSTEM_STATE_TRIGGERED
  }

  function _syncAlarmSystemCapabilities () {
    state.canArm =
      state.alarmSystem.alarms.every(alarm => alarm.capabilities?.arm === 'x')
    state.canDisarm = state.alarmSystem.capabilities?.disarm === 'x'
  }

  /**
   * @param {TBasAlarm} alarm
   * @param {int} armSeconds
   */
  function showInputModal (alarm, armSeconds) {
    let showCircleProgress
    state.selectedAlarm = alarm
    state.countdown = armSeconds

    if (_isDemo()) {
      showCircleProgress = !state.isArmed
    } else {
      showCircleProgress =
        state.alarmSystem.state === SECURITY.BAS_SYSTEM_STATE_DISARMED ||
        state.alarmSystem.state === SECURITY.BAS_SYSTEM_STATE_UNKNOWN
    }

    BasInputModal.show(
      BAS_INPUT_MODAL.T_SECURITY_NUMBER,
      {
        title: BasUtilities.translate('enter_keycode'),
        subTitle: (
          (
            state.alarmSystem.state === SECURITY.BAS_SYSTEM_STATE_ARMED ||
              state.alarmSystem.state === SECURITY.BAS_SYSTEM_STATE_TRIGGERED
          )
            ? SECURITY.BAS_ALARM_TYPE_DISARM
            : SECURITY.BAS_ALARM_TYPE_ARM
        ) +
          ' ' +
          _translateToLowerCase('alarm'),
        length: 4,
        min: 0,
        max: state.countdown,
        showCircleProgress: showCircleProgress
      }
    )
  }

  /**
   * @param {string} code
   */
  function arm (code) {
    return _getSecurityTopic()?.arm(
      state.alarmSystem.uuid,
      state.selectedAlarm.uuid,
      code
    )
  }

  function decrementCounter () {

    if (state.countdown < 0) {

      cancelCountdown()
      $rootScope.$applyAsync()

    } else {

      state.countdown--
      $rootScope.$emit(SECURITY.EVT_COUNTDOWN_UPDATED, state.countdown)
    }
  }
  function decrementCounterDemo () {

    const interval = setInterval(() => {
      decrementCounter()

      if (state.countdown <= 0) {
        state.isArmed = true
        state.alarmSystem.state = SECURITY.BAS_SYSTEM_STATE_ARMED
        $rootScope.$emit(SECURITY.EVT_SYSTEM_STATE_CHANGED)
        clearInterval(interval)
      }
    }, 1000)
  }

  function cancelCountdown (code) {
    state.countdown = -1
    if (code != null) {
      disarm(code)
      if (_isDemo()) {
        state.isArmed = false
        state.alarmSystem.state = SECURITY.BAS_SYSTEM_STATE_DISARMED
        $rootScope.$emit(SECURITY.EVT_SYSTEM_STATE_CHANGED)
      }
    }
  }

  /**
   * @param {string} code
   */
  function disarm (code) {
    const securityTopic = _getSecurityTopic()
    if (_isDemo()) {
      state.isArmed = false
      state.alarmSystem.state = SECURITY.BAS_SYSTEM_STATE_DISARMED
      $rootScope.$emit(SECURITY.EVT_SYSTEM_STATE_CHANGED)
    }
    return securityTopic.disarm(state.alarmSystem.uuid, code)
  }

  function isLockedOut () {
    return lockout.isActive && Date.now() < lockout.unlockAt
  }

  function getLockoutRemaining () {
    return lockout.isActive ? Math.max(0, lockout.unlockAt - Date.now()) : 0
  }

  function startLockout (durationMs) {
    lockout.isActive = true
    lockout.unlockAt = Date.now() + durationMs
    lockout.durationMs = durationMs

    if (lockout.timer) clearTimeout(lockout.timer)

    lockout.timer = setTimeout(() => {
      lockout.isActive = false
      lockout.timer = null
    }, durationMs)
  }

  function getLockoutDuration () {
    return lockout.durationMs
  }

  /**
   * @private
   * @returns {boolean}
   */
  function _isDemo () {
    return currentBasCoreState.core?.isDemo() ?? false
  }

  /**
   * @private
   * @returns {?SecurityTopic}
   */
  function _getSecurityTopic () {
    return currentBasCoreState.core?.core?.securityTopic
  }

  /**
   * @private
   * @param {string} key
   * @returns string
   */
  function _translateToLowerCase (key) {
    return BasUtilities.translate(key).toLowerCase()
  }

  /**
   * @returns {TSecurityAlarmState}
   */
  function get () {

    return state
  }

  function clearState () {
    state.alarmSystem = null
    state.selectedAlarm = null
    state.canArm = false
    state.canDisarm = false
    state.countdown = -1
  }
}
